import { useState, useEffect, useCallback } from "react";
import {
  Typography,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Edit, Delete, Search } from "@mui/icons-material";
import HotelSelector from "../../components/common/HotelSelector";
import AddUserForm from "../../components/forms/AddUser";
import EditUserModal from "../../components/forms/EditUser";
import { Hotel } from "../../types/hotel.d";
import { useHotels } from "../../hooks/useHotels";
import { useHotelUsers } from "../../hooks/useHotelUsers";
import { UpdateUserDTO, User } from "../../types/user.d";
import { apiCall } from "../../utils/apiCall";
import UserService from "../../api/userService";
import { useAuth } from "../../context/authContext";

const Contacts = () => {
  const hotels = useHotels();
  const { user } = useAuth();

  const [selectedHotel, setSelectedHotel] = useState<Hotel | null>(null);
  const { users, fetchUsers } = useHotelUsers(selectedHotel?.id || null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UpdateUserDTO | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (user?.role !== "Admin" && user?.hotel) {
      setSelectedHotel(user.hotel);
    }
  }, [user]);

  const loadUsers = useCallback(async () => {
    if (!selectedHotel) return;
    setIsLoadingUsers(true);
    await fetchUsers();
    setIsLoadingUsers(false);
  }, [selectedHotel, fetchUsers]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleHotelChange = useCallback((hotel: Hotel | null) => {
    setSelectedHotel(hotel);
  }, []);

  const handleEditUser = (user: UpdateUserDTO) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUserClick = (user: User) => {
    setUserToDelete(user);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (userToDelete) {
      const result = await apiCall(
        UserService.deleteUser(userToDelete.keycloakId)
      );
      if (result !== null) {
        loadUsers();
      }
      setIsDeleteConfirmOpen(false);
    }
  };

  const filteredUsers = users.filter((user) =>
    `${user.firstName} ${user.lastName} ${user.email} ${user.role} ${user.username}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  return (
    <Box
      mt={5}
      mb={5}
      px={3}
      display="flex"
      flexDirection="column"
      alignItems="center"
      width="100%"
    >
      <Typography variant="h5" mb={3}>
        Contacts
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="center"
        gap={2}
        mb={2}
        width="100%"
        maxWidth={1000}
      >
        {user?.role === "Admin" && (
          <HotelSelector
            hotels={hotels}
            selectedHotel={selectedHotel}
            onChange={handleHotelChange}
          />
        )}

        <Box minWidth={250}>
          <TextField
            fullWidth
            placeholder="Rechercher un contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {isLoadingUsers ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="150px"
          width="100%"
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ width: "100%", overflowX: "auto" }}
        >
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Nom</strong>
                </TableCell>
                <TableCell>
                  <strong>Prénom</strong>
                </TableCell>
                <TableCell>
                  <strong>Nom d'utilisateur</strong>
                </TableCell>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                <TableCell>
                  <strong>Rôle</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.keycloakId} hover>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.username}</TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {user.email}
                  </TableCell>
                  <TableCell sx={{ textTransform: "capitalize" }}>
                    {user.role}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton onClick={() => handleEditUser(user)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteUserClick(user)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredUsers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 2 }}>
                    Aucun contact trouvé.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Box mt={2}>
        <Button
          variant="contained"
          onClick={() => setIsAddUserModalOpen(true)}
          disabled={!selectedHotel}
        >
          Ajouter un contact
        </Button>
      </Box>

      <Dialog
        open={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
      >
        <DialogTitle>Ajouter un contact</DialogTitle>
        <DialogContent>
          <AddUserForm
            isOpen={isAddUserModalOpen}
            onClose={() => setIsAddUserModalOpen(false)}
            selectedHotel={selectedHotel}
            hotels={hotels}
            refreshUsers={loadUsers}
          />
        </DialogContent>
      </Dialog>

      {selectedUser && (
        <EditUserModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          user={selectedUser}
          hotels={hotels}
          selectedHotel={selectedHotel}
          refreshUsers={loadUsers}
        />
      )}

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmation de suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Voulez-vous vraiment supprimer {userToDelete?.firstName}{" "}
            {userToDelete?.lastName} ?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>Annuler</Button>
          <Button onClick={confirmDeleteUser} color="error" variant="contained">
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Contacts;
